import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';

interface WorkflowStepProps {
  stepNumber: number;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  isComplete: boolean;
  isInProgress?: boolean;
  optional?: boolean;
  onToggleComplete?: () => void;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const WorkflowStep: React.FC<WorkflowStepProps> = ({
  stepNumber,
  title,
  description,
  isComplete,
  isInProgress = false,
  optional = false,
  onToggleComplete,
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onChange,
}) => {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleAccordionChange = (event: React.SyntheticEvent, newExpanded: boolean) => {
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    if (onChange) {
      onChange(event, newExpanded);
    }
  };

  const getUncheckedIcon = () => {
    if (isInProgress) {
      return <CheckCircleIcon sx={{ color: theme.palette.bidlogiq.camel }} />;
    }
    if (optional) {
      return <CheckCircleIcon sx={{ color: theme.palette.grey[400] }} />;
    }
    return <RadioButtonUncheckedIcon />;
  };

  return (
    <Box sx={{ mb: 3, scrollMarginTop: '120px' }} id={`workflow-step-${stepNumber}`}>
      <Accordion
        expanded={isExpanded}
        onChange={handleAccordionChange}
        elevation={0}
        sx={{
          borderRadius: 2,
          position: 'relative', // Context for pseudo-element
          boxShadow: 'none',
          '&:first-of-type': {
            borderRadius: 2,
          },
          '&:last-of-type': {
            borderRadius: 2,
          },
          '&:before': {
            display: 'none',
          },
          // Pseudo-element to draw the border *on top* of content to prevent bleeding
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 2,
            border: `2px solid ${theme.palette.greyContrast}`,
            pointerEvents: 'none', // Allow clicks to pass through
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}
          aria-controls={`panel${stepNumber}-content`}
          id={`panel${stepNumber}-header`}
          sx={{
            backgroundColor: 'greyLight',
            color: 'text.primary',
            // Remove border from summary as it is handled by the parent overlay now,
            // or keep it if we want a separator when expanded.
            // Keeping separator logic but ensuring it doesn't conflict.
            borderBottom: isExpanded ? `2px solid ${theme.palette.greyContrast}` : 'none',
            borderRadius: 2,
            borderBottomLeftRadius: isExpanded ? 0 : 2,
            borderBottomRightRadius: isExpanded ? 0 : 2,
            '& .MuiTypography-root': { fontWeight: 'bold' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Step Number/Check */}
            <Box
              sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                icon={getUncheckedIcon()}
                checkedIcon={<CheckCircleIcon sx={{ color: '#2e7d32' }} />}
                checked={isComplete}
                onChange={onToggleComplete}
                sx={{
                  p: 0,
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: '#2e7d32',
                  },
                  '& .MuiSvgIcon-root': { fontSize: 28 },
                  cursor: onToggleComplete ? 'pointer' : 'default',
                  pointerEvents: onToggleComplete ? 'auto' : 'none',
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                {stepNumber}. {title}{' '}
                {optional && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ fontWeight: 'normal', color: 'text.secondary' }}
                  >
                    (Optional)
                  </Typography>
                )}
              </Typography>
              {description && (
                <Box sx={{ color: 'inherit', opacity: 0.8, fontSize: '1rem' }}>
                  {typeof description === 'string' ? (
                    <Typography variant="body1" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                      {description}
                    </Typography>
                  ) : (
                    description
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default WorkflowStep;
